import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, addMonths, isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { StatusBadge, money, jobTime } from "@/components/status";

export default function CalendarView({ jobs }) {
  const [cursor, setCursor] = useState(new Date());
  const navigate = useNavigate();

  const monthStart = startOfMonth(cursor);
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(endOfMonth(cursor)),
  });

  const jobsByDay = (d) =>
    jobs.filter((j) => {
      try { return isSameDay(parseISO(j.date), d); } catch { return false; }
    });

  return (
    <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="calendar-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-heading font-bold text-lg text-sapphire">{format(cursor, "MMMM yyyy")}</h3>
        <div className="flex gap-1">
          <button onClick={() => setCursor(addMonths(cursor, -1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-2 text-sm rounded hover:bg-slate-100" data-testid="cal-today">Today</button>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-border bg-slate-50">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-2 text-[11px] uppercase tracking-wide font-semibold text-muted-foreground text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const dayJobs = jobsByDay(d);
          const inMonth = isSameMonth(d, cursor);
          const today = isSameDay(d, new Date());
          let dayCls = "text-slate-400";
          if (today) dayCls = "bg-ocean text-white w-5 h-5 rounded-full flex items-center justify-center";
          else if (inMonth) dayCls = "text-sapphire";
          return (
            <div key={d.toISOString()} className={`min-h-[96px] border-b border-r border-border p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/50"}`}>
              <div className={`text-xs font-semibold mb-1 ${dayCls}`}>
                {format(d, "d")}
              </div>
              <div className="space-y-1">
                {dayJobs.slice(0, 3).map((j) => (
                  <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}
                    className="w-full text-left text-[10px] px-1 py-0.5 rounded bg-ocean/10 text-ocean font-medium truncate hover:bg-ocean/20 transition-colors"
                    data-testid={`cal-job-${j.id}`}>
                    {jobTime(j)} {j.property?.name}
                  </button>
                ))}
                {dayJobs.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{dayJobs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
